<script lang="ts">
import SuggestionInputMostSearched from "~/components/navigation/SuggestionInputMostSearched.vue";
import SuggestionInputProducts from "~/components/navigation/SuggestionInputProducts.vue";
import BaseTransition from "@/components/common/BaseTransition.vue";
import { HomeService } from "~/services/HomeService";
import { OnClickOutside } from "@vueuse/components";
import { getTitleTranslate } from "@/utils/general";
import { VITE_BANNER_SUGGEST } from "~/config/constants.js";
import eventBus from "~/event-bus";
import { nextTick } from "vue";
import { useWindowScroll } from "@vueuse/core";
import { CanceledError } from "axios";
import useSearchStore from "~/stores/search";
import googleAnalytics from "~/services/GoogleAnalyticsService";
import LazyImage from "../utils/LazyImage.vue";

export default {
  components: {
    SuggestionInputMostSearched,
    SuggestionInputProducts,
    BaseTransition,
    OnClickOutside,
    LazyImage,
  },
  props: {
    type: {
      type: String,
      default: "desktop",
    },
  },
  emits: ["get-results", "get-tabTitle", "close"],
  setup() {
    return {
      suggessTimeoutId: null as number | null,
      homeService: new HomeService(),
    };
  },
  data() {
    return {
      search: "",
      prevSearch: "",
      seeMoreProduct: "/",
      showSuggestion: false,
      isSubmittingSearch: false,
      preventNextSuggest: false,
      loading: false,
      products: [],
      mostSearched: [],
      banners: [] as { name: string; image: string }[],
      thereWasAproblem: false,
      windowScroll: useWindowScroll(),
      blurTid: undefined as number | undefined,
      blurScrollY: 0,
      elementOffsetHeight: 0,
      loadedBanners: false,
      searchStore: useSearchStore(),
    };
  },
  computed: {
    haveRecords() {
      if (this.products.length > 0 || this.mostSearched.length > 0) {
        return true;
      }
      return false;
    },
  },
  watch: {
    type(newType) {
      if (newType === "mobile") {
        this.setFocus();
      }
    },
    "searchStore.searchText"() {
      this.search = this.searchStore.searchText || "";
    },
    "searchStore.loading"(loading) {
      if (!loading) {
        this.preventNextSuggest = false;
      }
    },
    // Solicita por evento cerrar el input de búsqueda cuando el usuario scrollea.
    "windowScroll.y"(y) {
      const distanceToClose = 70;
      if (!this.blurTid) {
        this.blurTid = window.setTimeout(() => {
          const distance = Math.abs(this.blurScrollY - y);
          if (
            distance > distanceToClose &&
            this.search &&
            this.search.length === 0
          ) {
            this.$emit("close");
          }
          this.blurTid = undefined;
        }, 1000); // tiempo para cerrar el input de búsqueda si el usuario siguió scrolleando
      }
      this.blurScrollY = y;
    },
    "$route.name"(name) {
      if (name !== "search") {
        this.search = "";
      }
    },
    "$route.fullPath"() {
      this.showSuggestion = false;
      this.loading = false;
      this.isSubmittingSearch = false;
    },
  },
  mounted() {
    if (this.type === "mobile") {
      this.setFocus();
    }
    this.search = this.searchStore.searchText || "";
  },
  beforeUnmount() {
    eventBus.off("close-suggestions");
  },
  created() {
    this.homeService.getSuggestionInitData();
    eventBus.on("close-suggestions", (status = false) => {
      this.showSuggestion = status as boolean;
    });
  },
  methods: {
    getSuggestions() {
      const hadPrevCall = !!this.suggessTimeoutId;
      if (this.suggessTimeoutId) {
        clearTimeout(this.suggessTimeoutId);
        this.homeService.lastSuggestSignal?.abort();
      }
      this.suggessTimeoutId = window.setTimeout(
        async () => {
          this.suggessTimeoutId = null;
          if (this.preventNextSuggest || this.isSubmittingSearch) {
            this.preventNextSuggest = false;
            return;
          }
          this.loading = true;
          this.thereWasAproblem = false;
          this.showSuggestion = true;
          try {
            const searched = this.search;
            const response = await this.homeService.getSuggestions(searched);
            this.prevSearch = searched;
            this.seeMoreProduct = "buscar/t_" + searched;
            this.products = response.data.products;
            this.mostSearched = response.data.most_searched;
            this.loading = false;
            this.thereWasAproblem = false;
            if (!this.loadedBanners) {
              this.homeService
                .getBanners(VITE_BANNER_SUGGEST)
                .then((response) => {
                  if (response) {
                    this.banners = response;
                  }
                });
              this.loadedBanners = true;
            }
          } catch (err) {
            if (!(err instanceof CanceledError)) {
              this.loading = false;
              this.thereWasAproblem = true;
              console.error(err);
            }
          }
        },
        hadPrevCall ? 500 : 100,
      );
    },
    checkEnterToFullSearch() {
      if (this.search && this.search.length > 2) {
        this.fullSearch();
      }
    },
    searchSuggestions(ev: KeyboardEvent) {
      this.isSubmittingSearch = false;
      if (this.search && this.search.length > 2) {
        if (ev.code != "Enter" && ev.key != "Enter") {
          if (this.search.trim() === this.prevSearch.trim()) {
            // muestra los resultados previamente cargados
            this.showSuggestion = true;
          } else {
            if (!this.showSuggestion) {
              // quita los productos anteriores si ya se había ocultado y el texto nuevo es diferente para mostrar la carga
              this.products.splice(0);
            }
            this.getSuggestions();
          }
        }
      } else {
        this.showSuggestion = false;
      }
    },
    closeSuggestions() {
      this.showSuggestion = false;
    },
    getTitle(section: string, name: string) {
      return getTitleTranslate(section, name);
    },
    async fullSearch() {
      if (this.search?.trim() === this.searchStore.submitSearchText?.trim()) {
        return;
      }
      this.isSubmittingSearch = true;
      this.searchStore.$setSubmitSearchText(this.search);
      this.preventNextSuggest = true;
      if (this.suggessTimeoutId) {
        clearTimeout(this.suggessTimeoutId);
        this.suggessTimeoutId = null;
      }
      this.homeService.lastSuggestSignal?.abort();
      this.showSuggestion = false;
      this.loading = false;
      if (this.search.length === 0) {
        this.isSubmittingSearch = false;
        this.$emit("close");
      }
      googleAnalytics.search(this.search);
    },
    formatText(input: string) {
      const words = input.trim().split(/\s+/);
      return words.length > 1 ? words.join("-") : input;
    },
    setFocus() {
      nextTick(() => {
        const input = this.$refs.searchInput as HTMLInputElement;
        if (input) {
          input.focus();
        }
      });
    },
    onFocus(_ev: FocusEvent) {
      this.isSubmittingSearch = false;
      if (
        this.search.length > 2 &&
        this.search.trim() === this.prevSearch.trim() &&
        this.products.length
      ) {
        this.showSuggestion = true;
      }
    },
    clearText() {
      this.isSubmittingSearch = false;
      this.search = "";
      this.showSuggestion = false;
      this.products.splice(0, this.products.length);
    },
  },
};
</script>

<template>
  <div
    class="w-100 position-relative"
    :class="type == 'mobile' ? 'mb-1' : 'translate-middle-y top-50'"
  >
    <div
      v-if="type == 'mobile'"
      ref="inputContainer"
      class="input-group mb-1 mb-lg-3 btn-suggestion"
    >
      <input
        ref="searchInput"
        v-model="search"
        type="text"
        class="form-control rounded-start-5 border-0 ring-none"
        :placeholder="getTitle('header', 'what_are_you_looking_for')"
        :aria-label="getTitle('header', 'what_are_you_looking_for')"
        @keyup="searchSuggestions"
        @keyup.enter="checkEnterToFullSearch"
        @keydown.enter="checkEnterToFullSearch"
        @blur="search.length === 0 ? $emit('close') : null"
        @focus="onFocus"
      />
      <span
        class="input-group-text rounded-end-5 bg-white border-0"
        @click="clearText"
      >
        <i v-if="search?.length" class="bi bi-x-circle-fill"></i>
      </span>
    </div>
    <div v-else class="input-group btn-suggestion">
      <input
        v-model="search"
        type="text"
        class="input-search-home bg-light"
        :placeholder="getTitle('header', 'what_are_you_looking_for')"
        aria-describedby="serch_product"
        @keyup="searchSuggestions"
        @keyup.enter="checkEnterToFullSearch"
        @keydown.enter="checkEnterToFullSearch"
        @focus="onFocus"
      />
      <button
        id="serch_product"
        type="button"
        class="z-1 btn btn-danger rounded-0 bg-danger position-relative"
        aria-label="Buscar productos"
        @click="fullSearch"
      >
        <span
          class="text-black position-absolute end-100 top-middle pointer px-3 py-1"
          @click="clearText"
        >
          <i v-if="search?.length" class="bi bi-x-circle-fill"></i>
        </span>
        <span
          v-if="isSubmittingSearch"
          class="spinner-border spinner-border-sm text-white"
          role="status"
          aria-hidden="true"
        ></span>
        <i v-else class="bi bi-search px-2 fs-5" />
      </button>
    </div>
    <base-transition>
      <OnClickOutside
        :options="{ ignore: ['.btn-suggestion'] }"
        @trigger="closeSuggestions"
      >
        <div
          v-if="showSuggestion && !isSubmittingSearch && !searchStore.loading"
          class="menu-suggestion position-absolute start-50 translate-middle-x border border-light-subtle shadow p-2 bg-white rounded-3"
        >
          <div
            v-if="loading && !products.length"
            class="d-flex justify-content-center align-items-center p-4"
          >
            <div class="spinner-border text-secondary" role="status">
              <span class="visually-hidden">{{ $t("Loading") }}...</span>
            </div>
          </div>
          <div v-else-if="thereWasAproblem" class="text-danger mt-3">
            <p class="ms-2">
              Hubo un problema con la solicitud, intenta de nuevo más tarde.
              <a
                class="ms-1 text-danger fw-semibold text-decoration-none"
                @click="getSuggestions()"
              >
                Volver a intentar.
              </a>
            </p>
          </div>

          <div
            v-else-if="!loading && !haveRecords"
            class="p-4 text-secondary text-center"
          >
            No hay sugerencias rápidas pero encontrarás productos al buscar.
          </div>
          <div v-if="products.length">
            <suggestion-input-most-searched
              v-if="mostSearched.length"
              :most-searched="mostSearched"
              class="mb-2"
            />
            <suggestion-input-products
              v-if="products.length"
              :products="products"
              :see-more="seeMoreProduct"
            />
          </div>
          <div
            class="py-2 d-flex flex-column flex-md-row gap-2 justify-content-center align-items-center text-center overflow-hidden"
          >
            <lazy-image
              v-for="(banner, index) in banners"
              :key="index"
              class="img-fluid image-banner"
              :src="banner.image"
              :alt="banner.name"
              width="360"
              height="135"
            />
          </div>
        </div>
      </OnClickOutside>
    </base-transition>
  </div>
</template>

<style lang="scss">
.menu-suggestion {
  top: calc(100% + 1px);
  z-index: 9999;
  height: auto;
  max-height: 75vh;
  width: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  position: absolute;
  width: 100%;
  @media (min-width: 992px) {
    width: 65vw;
    top: calc(100% + 20px) !important;
  }
  @media (min-width: 1200px) {
    width: 75vw;
  }
}

.ring-none {
  box-shadow: unset !important;
}

.menu-suggestion-tabs {
  width: 300%;

  @media (min-width: 768px) {
    width: 100%;
  }
}

.input-search-home {
  width: 379px;
  border: 1px solid #e5e5e5;
  outline: none;
  padding: 10px;

  @media (min-width: 992px) {
    max-width: 75%;
  }

  @media (min-width: 1200px) {
    max-width: 80%;
  }

  @media (min-width: 1400px) {
    max-width: 85%;
    height: 57px;
  }

  @media (min-width: 1700px) {
    max-width: 100%;
    height: 64px;
  }

  &::placeholder {
    font: normal normal normal 16px/18px Titillium Web;
  }
}
</style>
